// FlowDiagramBlock.tsx
//
// A reusable React Flow architecture-diagram block for BlockRenderer KB pages.
// Thin-lined, clean, zoomable, label-rich — the dashboard's native diagram
// standard (@xyflow/react), matching MeetingsSystemArchitecturePage's aesthetic.
//
// Usage in a recipe — two modes:
//   1. Preset:  { "type": "FlowDiagram", "props": { "which": "system" } }
//      Renders one of the built-in DIAGRAMS below (data typed here, recipe tiny).
//   2. Inline:  { "type": "FlowDiagram", "props": { "nodes": [...], "edges": [...] } }
//      The recipe supplies its own nodes/edges, so an arbitrary process flow
//      can be authored as pure page_content data — no code change per diagram.
//      When `nodes` is present it wins; otherwise the `which` preset renders.
//
// Edge handles (inline mode): each edge may set `sh` (source handle) and `th`
// (target handle) to steer where the arrow leaves/enters a box:
//   • "b" = bottom (source), "t" = top (target)  — the default vertical flow
//   • "r"/"l" = the node's horizontal handles (right source / left target)
// Use "r"/"l" for side branches (1→many) and feedback loops so the arrows route
// around the spine instead of stacking on top of it.

import {
  ReactFlow,
  type Node,
  type Edge,
  type NodeTypes,
  Handle,
  Position,
  Background,
  Controls,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const T = {
  ink: "#1C1917",
  ink2: "#57534E",
  line: "#EADDD0",
  accent: "#C4602A",
  peachDark: "#E8814E",
  peachSoft: "#FAEFE5",
  green: "#2A7A4B",
  greenSoft: "#E8F3EC",
  blue: "#2E6FB6",
  blueSoft: "#E5EEF8",
  violet: "#7c3aed",
  violetSoft: "#EDE7F8",
  gray: "#475569",
  graySoft: "#EDF1F5",
};
const FONT = "Inter, system-ui, -apple-system, sans-serif";
const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';

type Tone = "operator" | "jarvis" | "engine" | "agent" | "store" | "action";

const TONES: Record<Tone, { border: string; bg: string; text: string; chip: string }> = {
  operator: { border: T.gray, bg: T.graySoft, text: "#1f2937", chip: T.gray },
  jarvis: { border: T.peachDark, bg: T.peachSoft, text: T.accent, chip: T.peachDark },
  engine: { border: T.violet, bg: T.violetSoft, text: "#3b1d8a", chip: T.violet },
  agent: { border: T.blue, bg: T.blueSoft, text: "#0b3d7a", chip: T.blue },
  store: { border: T.green, bg: T.greenSoft, text: "#0e4d2c", chip: T.green },
  action: { border: T.accent, bg: "#FFFFFF", text: T.accent, chip: T.accent },
};

type FlowNodeData = {
  step?: number;
  title: string;
  subtitle?: string;
  tone: Tone;
};

function FlowNode({ data }: { data: FlowNodeData }) {
  const c = TONES[data.tone];
  return (
    <div
      style={{
        borderRadius: 10,
        border: `1.5px solid ${c.border}`,
        background: c.bg,
        padding: "9px 12px",
        width: 210,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        position: "relative",
        fontFamily: FONT,
      }}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0, width: 1, height: 1 }} />
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0, width: 1, height: 1 }} />
      {data.step !== undefined && (
        <div
          style={{
            position: "absolute",
            top: -10,
            left: -10,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: c.chip,
            color: "white",
            fontSize: 12,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          }}
        >
          {data.step}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 700, color: c.text, lineHeight: 1.25 }}>
        {data.title}
      </div>
      {data.subtitle && (
        <div style={{ fontSize: 10.5, color: T.ink2, marginTop: 3, lineHeight: 1.35, fontFamily: MONO }}>
          {data.subtitle}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ opacity: 0, width: 1, height: 1 }} />
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0, width: 1, height: 1 }} />
    </div>
  );
}

const nodeTypes: NodeTypes = { fnode: FlowNode };

function n(
  id: string,
  x: number,
  y: number,
  tone: Tone,
  title: string,
  subtitle?: string,
  step?: number,
): Node {
  return { id, type: "fnode", position: { x, y }, data: { title, subtitle, tone, step } };
}

function e(
  id: string,
  source: string,
  target: string,
  opts: { label?: string; color?: string; dashed?: boolean; sh?: string; th?: string } = {},
): Edge {
  const color = opts.color ?? T.gray;
  return {
    id,
    source,
    target,
    sourceHandle: opts.sh,
    targetHandle: opts.th,
    type: "smoothstep",
    label: opts.label,
    labelStyle: { fontSize: 10, fontFamily: FONT, fill: T.ink2, fontWeight: 600 },
    labelBgStyle: { fill: "#FFFFFF", fillOpacity: 0.9 },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 4,
    style: { stroke: color, strokeWidth: 1.5, strokeDasharray: opts.dashed ? "5 4" : undefined },
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
  };
}

// ── Diagram 1: high-level system ────────────────────────────────────────────
const systemNodes: Node[] = [
  n("erez", 470, 0, "operator", "Erez", "operator · voice + decisions"),
  n("jarvis", 470, 120, "jarvis", "Jarvis", "one terminal · thinks + orchestrates · persistent session"),
  n("pc", 470, 250, "engine", "Paperclip", "the company · scheduler + runs · Postgres"),
  n("atlas", 20, 390, "agent", "Atlas", "Projects agent"),
  n("nova", 250, 390, "agent", "Nova", "Invoicing / decks"),
  n("ben", 480, 390, "agent", "Ben", "R&D"),
  n("emma", 710, 390, "agent", "Emma", "El HaDegel"),
  n("ws", 60, 540, "store", "Shared workspace", "~/Workspace/jarvis-pai · CLAUDE.md (fresh every run)"),
  n("mcp", 360, 540, "engine", "My Jarvis MCP", "tools bridge · query + write + voice"),
  n("brain", 700, 540, "store", "The Brain", "Dashboard KB + memory · Neon"),
  n("dash", 360, 680, "store", "Dashboard", "where work ships · preview → main"),
  n("voice", 60, 680, "action", "Voice feed", "Kokoro TTS · plays for Erez"),
];
const systemEdges: Edge[] = [
  e("s1", "erez", "jarvis", { label: "message", color: T.gray, sh: "b", th: "t" }),
  e("s2", "jarvis", "pc", { label: "opens + assigns issues", color: T.accent, sh: "b", th: "t" }),
  e("s3", "pc", "atlas", { label: "wakes", color: T.violet, sh: "b", th: "t" }),
  e("s4", "pc", "nova", { color: T.violet, sh: "b", th: "t" }),
  e("s5", "pc", "ben", { color: T.violet, sh: "b", th: "t" }),
  e("s6", "pc", "emma", { color: T.violet, sh: "b", th: "t" }),
  e("s7", "atlas", "ws", { label: "runs here · loads CLAUDE.md", color: T.green, sh: "b", th: "t" }),
  e("s8", "atlas", "mcp", { color: T.blue, sh: "b", th: "t" }),
  e("s9", "mcp", "brain", { label: "query + write", color: T.green }),
  e("s10", "mcp", "dash", { label: "ships", color: T.green, sh: "b", th: "t" }),
  e("s11", "jarvis", "ws", { label: "same folder", color: T.green, dashed: true, sh: "b", th: "t" }),
  e("s12", "jarvis", "voice", { label: "speak", color: T.peachDark, dashed: true, sh: "b", th: "t" }),
  e("s13", "voice", "erez", { label: "hears", color: T.peachDark, dashed: true }),
];

// ── Diagram 2: the work loop (process flow) ──────────────────────────────────
const flowNodes: Node[] = [
  n("f1", 360, 0, "jarvis", "Decide the work", "Erez + Jarvis, in the terminal", 1),
  n("f2", 360, 120, "jarvis", "Open + assign issue", "Jarvis → Paperclip (a message)", 2),
  n("f3", 360, 240, "engine", "Wake the agent", "message · heartbeat · routine → a run", 3),
  n("f4", 360, 360, "agent", "Resume + load contract", "--resume session · CLAUDE.md from jarvis-pai", 4),
  n("wsF", 20, 360, "store", "Shared workspace", "~/Workspace/jarvis-pai"),
  n("f5", 360, 480, "agent", "Query the brain", "KB · memory via MCP", 5),
  n("brainF", 700, 480, "store", "The Brain", "Neon · KB + memory"),
  n("f6", 360, 600, "agent", "Build via MCP", "write → preview branch → CF build", 6),
  n("f7", 360, 720, "action", "Verify + ship", "poll hash URL → ship preview → main", 7),
  n("dashF", 700, 720, "store", "Dashboard", "live"),
  n("f8", 360, 840, "jarvis", "Report + voice; sleep", "comment on issue · send_voice_message", 8),
];
const flowEdges: Edge[] = [
  e("e1", "f1", "f2", { color: T.accent, sh: "b", th: "t" }),
  e("e2", "f2", "f3", { color: T.accent, sh: "b", th: "t" }),
  e("e3", "f3", "f4", { color: T.violet, sh: "b", th: "t" }),
  e("e4", "f4", "wsF", { label: "loads", color: T.green }),
  e("e5", "f4", "f5", { color: T.blue, sh: "b", th: "t" }),
  e("e6", "f5", "brainF", { label: "read", color: T.green }),
  e("e7", "f5", "f6", { color: T.blue, sh: "b", th: "t" }),
  e("e8", "f6", "f7", { color: T.blue, sh: "b", th: "t" }),
  e("e9", "f7", "dashF", { label: "ships", color: T.green }),
  e("e10", "f7", "f8", { color: T.accent, sh: "b", th: "t" }),
  e("loop", "f8", "f3", { label: "Erez replies in chat → same session resumes", color: T.gray, dashed: true }),
];

const DIAGRAMS: Record<string, { nodes: Node[]; edges: Edge[]; height: number }> = {
  system: { nodes: systemNodes, edges: systemEdges, height: 580 },
  flow: { nodes: flowNodes, edges: flowEdges, height: 640 },
};

// ── Inline recipe shapes (mode 2) ───────────────────────────────────────────
// Mirror the n()/e() helper signatures but as plain JSON the recipe can carry.
type InlineNode = {
  id: string;
  x: number;
  y: number;
  tone?: Tone;
  title: string;
  subtitle?: string;
  step?: number;
};
type InlineEdge = {
  id?: string;
  source: string;
  target: string;
  label?: string;
  color?: string;
  dashed?: boolean;
  sh?: string;
  th?: string;
};

// Translate a recipe handle hint into an @xyflow handle id.
// Source handles on a node: Right (no id) + Bottom (id "b").
// Target handles on a node: Left (no id) + Top (id "t").
// "r"/"l" → undefined picks the no-id (horizontal) handle; default is vertical.
function srcHandle(v?: string): string | undefined {
  if (v === "r" || v === "l" || v === "right" || v === "left") return undefined;
  return "b";
}
function tgtHandle(v?: string): string | undefined {
  if (v === "l" || v === "r" || v === "left" || v === "right") return undefined;
  return "t";
}

export function FlowDiagram({
  which = "system",
  nodes: inlineNodes,
  edges: inlineEdges,
  title,
  caption,
  height,
}: {
  which?: "system" | "flow";
  nodes?: InlineNode[];
  edges?: InlineEdge[];
  title?: string;
  caption?: string;
  height?: number;
}) {
  // Inline-data path: when the recipe supplies its own nodes, build the diagram
  // from them via the same n()/e() helpers the presets use (so custom flows
  // stay on-brand). Edges default to a vertical bottom→top flow; set sh/th to
  // "r"/"l" for side branches and loops. Falls back to DIAGRAMS[which] when no
  // inline nodes are given.
  const custom = Array.isArray(inlineNodes) && inlineNodes.length > 0;
  const d = custom
    ? {
        nodes: inlineNodes.map((nd) =>
          n(
            nd.id,
            nd.x,
            nd.y,
            nd.tone && TONES[nd.tone] ? nd.tone : "agent",
            nd.title,
            nd.subtitle,
            nd.step,
          ),
        ),
        edges: (inlineEdges ?? []).map((ed, i) =>
          e(ed.id ?? `e${i}`, ed.source, ed.target, {
            label: ed.label,
            color: ed.color,
            dashed: ed.dashed,
            sh: srcHandle(ed.sh),
            th: tgtHandle(ed.th),
          }),
        ),
        height: height ?? 560,
      }
    : DIAGRAMS[which] ?? DIAGRAMS.system;
  return (
    <figure style={{ margin: "28px 0" }}>
      {title && (
        <figcaption
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: T.accent,
            fontFamily: FONT,
            marginBottom: 10,
          }}
        >
          {title}
        </figcaption>
      )}
      <div
        style={{
          border: `1px solid ${T.line}`,
          borderRadius: 14,
          overflow: "hidden",
          height: height ?? d.height,
          background: "#FDFBF8",
        }}
      >
        <ReactFlow
          nodes={d.nodes}
          edges={d.edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.12 }}
          minZoom={0.2}
          maxZoom={1.5}
          zoomOnScroll
          panOnDrag
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} size={1.2} color="#EADDD0" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      {caption && (
        <figcaption style={{ fontSize: 12, color: T.ink2, marginTop: 10, lineHeight: 1.5, fontFamily: FONT, fontStyle: "italic" }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

export default FlowDiagram;
